import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../../contexts/AuthContext";

function TransferMarketTotals() {
  const [totals, setTotals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [loadingDetails, setLoadingDetails] = useState(null);
  const [expandedUserId, setExpandedUserId] = useState(null);
  const [userDetails, setUserDetails] = useState({});
  const { apiBaseUrl } = useAuth();

  const fetchTotals = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        apiBaseUrl + "/api/transfer-market/user/totals"
      );

      const sortedData = (response.data.data || [])
        .map((item) => ({
          ...item,
          price: parseFloat(item.price),
        }))
        .sort((a, b) => b.price - a.price)
        .map((item, index) => ({
          ...item,
          rank: index + 1,
        }));

      setTotals(sortedData);
      setLoading(false);
    } catch (error) {
      console.error("❌ خطا در دریافت مجموع امتیازات:", error);
      setLoading(false);
    }
  };

  const handleViewDetails = async (userId) => {
    // اگر روی همون ردیف کلیک شد، بسته بشه
    if (expandedUserId === userId) {
      setExpandedUserId(null);
      return;
    }

    setLoadingDetails(userId);
    setExpandedUserId(userId);

    // اگر قبلاً دیتا رو گرفتیم، دوباره درخواست نزنیم
    if (userDetails[userId]) {
      setLoadingDetails(null);
      return;
    }

    try {
      const response = await axios.get(
        `${apiBaseUrl}/api/transfer-market/user/sub-values/${userId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      console.log(response);

      const userData = response.data.data[0];
      setUserDetails((prev) => ({
        ...prev,
        [userId]: userData,
      }));
    } catch (error) {
      console.error(`❌ خطا در دریافت جزئیات کاربر ${userId}:`, error);
    } finally {
      setLoadingDetails(null);
    }
  };

  useEffect(() => {
    fetchTotals();
  }, []);

  const filteredTotals = totals.filter((item) =>
    item.user_id.toString().includes(searchTerm)
  );

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div
          className="w-20 h-20 rounded-2xl flex items-center justify-center mb-6 relative"
          style={{
            background: "linear-gradient(135deg, #a855f7 0%, #7c3aed 100%)",
            boxShadow: "0 0 40px rgba(168, 85, 247, 0.5)",
          }}
        >
          <div className="animate-spin rounded-xl h-12 w-12 border-4 border-white/30 border-t-white"></div>
          <div
            className="absolute inset-0 rounded-2xl animate-ping opacity-75"
            style={{
              background: "linear-gradient(135deg, #a855f7 0%, #7c3aed 100%)",
            }}
          />
        </div>
        <span className="text-white text-lg font-bold">در حال بارگذاری...</span>
        <span className="text-purple-400 text-sm mt-2">لطفاً صبر کنید</span>
      </div>
    );
  }

  const totalAmount = totals.reduce((sum, item) => sum + item.price, 0);
  const averageAmount = totals.length > 0 ? totalAmount / totals.length : 0;
  const topThree = totals.slice(0, 3);

  return (
    <div className="min-h-screen p-6 relative overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div
          className="absolute rounded-full opacity-10 blur-3xl animate-pulse"
          style={{
            width: "600px",
            height: "600px",
            background: "radial-gradient(circle, #a855f7 0%, transparent 70%)",
            top: "-200px",
            right: "-100px",
            animationDuration: "4s",
          }}
        />
        <div
          className="absolute rounded-full opacity-10 blur-3xl animate-pulse"
          style={{
            width: "500px",
            height: "500px",
            background: "radial-gradient(circle, #7c3aed 0%, transparent 70%)",
            bottom: "-150px",
            left: "-100px",
            animationDuration: "5s",
            animationDelay: "1s",
          }}
        />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto space-y-8">
        {/* Header with Search */}
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6">
          <div>
            <h3 className="text-3xl font-black text-white mb-2">
              🏆 رده‌بندی Transfer Market
            </h3>
            <p className="text-purple-400 font-semibold">
              برترین تریدر های بازار نقل و انتقالات
            </p>
          </div>
          <div className="relative w-full lg:w-80">
            <input
              type="text"
              placeholder="جستجوی شناسه کاربر..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-gray-800/50 text-white rounded-xl pl-12 pr-4 py-3 border-2 border-purple-500/30 focus:border-purple-500 focus:outline-none transition-all backdrop-blur-sm"
              style={{
                boxShadow: "0 0 20px rgba(168, 85, 247, 0.2)",
              }}
            />
            <svg
              className="w-5 h-5 text-purple-400 absolute left-4 top-1/2 transform -translate-y-1/2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
        </div>

        {/* Leaderboard Table */}
        <div
          className="rounded-2xl overflow-hidden"
          style={{
            background:
              "linear-gradient(135deg, rgba(17, 24, 39, 0.8) 0%, rgba(31, 41, 55, 0.8) 100%)",
            border: "1px solid rgba(168, 85, 247, 0.3)",
            boxShadow: "0 8px 32px rgba(168, 85, 247, 0.2)",
            backdropFilter: "blur(20px)",
          }}
        >
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr
                  style={{
                    background:
                      "linear-gradient(135deg, rgba(168, 85, 247, 0.2) 0%, rgba(124, 58, 237, 0.2) 100%)",
                    borderBottom: "2px solid rgba(168, 85, 247, 0.3)",
                  }}
                >
                  <th className="px-6 py-5 text-right text-sm font-black text-purple-400">
                    رتبه
                  </th>
                  <th className="px-6 py-5 text-right text-sm font-black text-purple-400">
                    کاربر
                  </th>
                  <th className="px-6 py-5 text-right text-sm font-black text-purple-400">
                    ارزش ترنسفر
                  </th>
                  <th className="px-6 py-5 text-right text-sm font-black text-purple-400">
                    عملیات
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredTotals.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="px-6 py-16 text-center">
                      <div className="flex flex-col items-center gap-4">
                        <div
                          className="w-20 h-20 rounded-xl flex items-center justify-center"
                          style={{
                            background: "rgba(168, 85, 247, 0.1)",
                            border: "2px dashed rgba(168, 85, 247, 0.3)",
                          }}
                        >
                          <svg
                            className="w-10 h-10 text-purple-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                            />
                          </svg>
                        </div>
                        <p className="text-gray-400 text-lg">
                          نتیجه‌ای یافت نشد
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredTotals.map((item, index) => {
                    const isTop3 = item.rank <= 3;
                    const medalEmoji =
                      item.rank === 1
                        ? "🥇"
                        : item.rank === 2
                        ? "🥈"
                        : item.rank === 3
                        ? "🥉"
                        : "";
                    const isExpanded = expandedUserId === item.user_id;
                    const details = userDetails[item.user_id];

                    return (
                      <React.Fragment key={item.user_id}>
                        <tr
                          className="hover:bg-purple-500/10 transition-all duration-200 border-b border-gray-700/50"
                          style={{
                            animation: `slideIn 0.4s ease-out ${
                              index * 0.05
                            }s backwards`,
                          }}
                        >
                          <td className="px-6 py-5">
                            <div className="flex items-center gap-3">
                              <div
                                className="w-11 h-11 rounded-xl flex items-center justify-center font-black text-base relative group"
                                style={{
                                  background: isTop3
                                    ? "linear-gradient(135deg, rgba(168, 85, 247, 0.3) 0%, rgba(124, 58, 237, 0.3) 100%)"
                                    : "rgba(75, 85, 99, 0.3)",
                                  border: isTop3
                                    ? "2px solid rgba(168, 85, 247, 0.5)"
                                    : "1px solid rgba(75, 85, 99, 0.5)",
                                  color: "#fff",
                                  boxShadow: isTop3
                                    ? "0 4px 15px rgba(168, 85, 247, 0.3)"
                                    : "none",
                                }}
                              >
                                {item.rank}
                              </div>
                              {medalEmoji && (
                                <span className="text-2xl">{medalEmoji}</span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-5">
                            <div className="flex items-center gap-3">
                              <div
                                className="w-10 h-10 rounded-lg flex items-center justify-center"
                                style={{
                                  background:
                                    "linear-gradient(135deg, rgba(168, 85, 247, 0.2) 0%, rgba(124, 58, 237, 0.2) 100%)",
                                }}
                              >
                                <svg
                                  className="w-5 h-5 text-purple-400"
                                  fill="currentColor"
                                  viewBox="0 0 20 20"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                              </div>
                              <span className="text-white font-bold text-lg">
                                {item.user?.first_name +
                                  " " +
                                  item.user?.last_name}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-5">
                            <div className="flex items-center gap-3">
                              <svg
                                className="w-6 h-6 text-purple-400"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2.5}
                                  d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                />
                              </svg>
                              <span className="text-purple-300 font-black text-xl">
                                {item.price.toLocaleString()}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-5">
                            <button
                              onClick={() => handleViewDetails(item.user_id)}
                              disabled={loadingDetails === item.user_id}
                              className="px-4 py-2 rounded-lg font-bold text-sm transition-all duration-200 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed hover:scale-105 active:scale-95"
                              style={{
                                background:
                                  "linear-gradient(135deg, rgba(168, 85, 247, 0.3) 0%, rgba(124, 58, 237, 0.3) 100%)",
                                border: "1px solid rgba(168, 85, 247, 0.5)",
                                color: "#c084fc",
                                boxShadow: "0 4px 15px rgba(168, 85, 247, 0.2)",
                              }}
                            >
                              {loadingDetails === item.user_id ? (
                                <>
                                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-purple-400 border-t-transparent"></div>
                                  <span>در حال بارگذاری...</span>
                                </>
                              ) : (
                                <>
                                  <svg
                                    className={`w-4 h-4 transition-transform duration-200 ${
                                      isExpanded ? "rotate-180" : ""
                                    }`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M19 9l-7 7-7-7"
                                    />
                                  </svg>
                                  <span>
                                    {isExpanded ? "بستن جزئیات" : "مشاهده جزئیات"}
                                  </span>
                                </>
                              )}
                            </button>
                          </td>
                        </tr>

                        {/* Expanded Row */}
                        {isExpanded && details && (
                          <tr>
                            <td
                              colSpan="4"
                              className="px-6 py-0"
                              style={{
                                background:
                                  "linear-gradient(135deg, rgba(168, 85, 247, 0.05) 0%, rgba(124, 58, 237, 0.05) 100%)",
                              }}
                            >
                              <div
                                className="py-6"
                                style={{
                                  animation: "expandDown 0.3s ease-out",
                                }}
                              >
                                <div className="space-y-3">
                                  {details.values.map((value, idx) => (
                                    <div
                                      key={value.id}
                                      className="rounded-xl p-4"
                                      style={{
                                        background: "rgba(31, 41, 55, 0.6)",
                                        border: "1px solid rgba(75, 85, 99, 0.5)",
                                        animation: `slideIn 0.3s ease-out ${
                                          idx * 0.1
                                        }s backwards`,
                                      }}
                                    >
                                      <div className="flex items-center justify-between gap-4">
                                        <div className="flex-1">
                                          <p className="text-gray-300 text-sm leading-relaxed">
                                            {value.description}
                                          </p>
                                         
                                        </div>
                                        <div
                                          className="px-4 py-2 rounded-lg text-center flex-shrink-0"
                                          style={{
                                            background:
                                              "linear-gradient(135deg, rgba(168, 85, 247, 0.2) 0%, rgba(124, 58, 237, 0.2) 100%)",
                                            border:
                                              "1px solid rgba(168, 85, 247, 0.3)",
                                          }}
                                        >
                                          <div className="flex items-center gap-2">
                                            <svg
                                              className="w-5 h-5 text-purple-400"
                                              fill="none"
                                              stroke="currentColor"
                                              viewBox="0 0 24 24"
                                            >
                                              <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                              />
                                            </svg>
                                            <span className="text-purple-300 font-bold text-lg whitespace-nowrap">
                                              {parseFloat(
                                                value.weighted_value
                                              ).toLocaleString()}
                                            </span>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes podiumUp {
          from {
            opacity: 0;
            transform: translateY(50px) scale(0.9);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateX(-20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes expandDown {
          from {
            opacity: 0;
            max-height: 0;
          }
          to {
            opacity: 1;
            max-height: 1000px;
          }
        }
      `}</style>
    </div>
  );
}

export default TransferMarketTotals;